import React, { useEffect, useState } from "react";
import { Alert, Stack, TextField, Typography } from "@mui/material";
import { useIsGreek } from "../../context/LanguageContext";

export default function DetailedBifacialSettings({ settings, onChange, disabled }) {
  const greek = useIsGreek();
  const savedStructure = settings.bifacial?.structureLossPct ?? 5;
  const savedMismatch = settings.bifacial?.mismatchLossPct ?? 10;
  const [values, setValues] = useState({ structureLossPct: savedStructure, mismatchLossPct: savedMismatch });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setValues({ structureLossPct: savedStructure, mismatchLossPct: savedMismatch }); }, [savedStructure, savedMismatch]);
  const valid = value => value !== "" && Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 100;
  const save = async (key) => {
    if (saving || !valid(values[key])) return;
    const value = Number(values[key]);
    if (value === (key === "structureLossPct" ? savedStructure : savedMismatch)) return;
    setSaving(true); setError("");
    try { await onChange({ ...settings, bifacial: { structureLossPct: savedStructure, mismatchLossPct: savedMismatch, ...settings.bifacial, [key]: value } }); }
    catch { setError(greek ? "Οι πίσω απώλειες δεν αποθηκεύτηκαν. Δοκιμάστε ξανά." : "Rear allowances were not saved. Please try again."); }
    finally { setSaving(false); }
  };
  return <Stack spacing={1} sx={{ mt: 1 }}>
    <Alert severity="info">
      <Typography variant="body2" fontWeight={700}>{greek ? "Πίσω παραγωγή — μοντέλο σειρών" : "Rear production — row model"}</Typography>
      <Typography variant="body2">{greek
        ? "Η πίσω ακτινοβολία προστίθεται σε κάθε κυψέλη πριν από τον υπολογισμό διόδων και MPPT. Θεωρείται ομοιόμορφη ανά προσανατολισμό, για κανονικές σειρές σε επίπεδο έδαφος. Δεν επιλύονται οι επιμέρους πίσω σκιές από βάσεις, εμπόδια ή άλλους προσανατολισμούς ούτε τα άκρα των σειρών."
        : "Rear irradiance is added to each cell before the diode and MPPT calculation. It is uniform per orientation, assuming regular rows on level ground. Individual rear shadows from supports, obstacles or other orientations and row-end effects are not resolved."}</Typography>
      <Typography variant="body2">{greek
        ? "Οι παρακάτω παραδοχές μειώνουν μόνο την πίσω συνεισφορά, διαδοχικά. Προεπιλογές: 5% για βάσεις και 10% για ανομοιομορφία· προσαρμόστε τις με τεκμηρίωση της εγκατάστασης. Η θερμοκρασία βασίζεται στην μπροστινή ακτινοβολία, όπως στην τυπική μέθοδο."
        : "The allowances below reduce only the rear contribution, sequentially. Defaults: 5% for structures and 10% for non-uniformity; adjust using site evidence. Temperature uses front irradiance, as in the standard method."}</Typography>
    </Alert>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
      {[["structureLossPct", greek ? "Πίσω σκίαση βάσεων (%)" : "Rear structure shading (%)"],
        ["mismatchLossPct", greek ? "Πίσω ανομοιομορφία (%)" : "Rear non-uniformity (%)"]].map(([key, label]) =>
        <TextField key={key} label={label} type="number" size="small" value={values[key]}
          disabled={disabled || saving} inputProps={{ min: 0, max: 100, step: 0.5 }}
          error={!valid(values[key])} helperText={!valid(values[key]) ? (greek ? "Τιμή από 0 έως 100." : "Enter 0 to 100.") : undefined}
          onChange={event => setValues(previous => ({ ...previous, [key]: event.target.value }))}
          onBlur={() => save(key)} />)}
    </Stack>
    {error && <Alert severity="error">{error}</Alert>}
  </Stack>;
}
